import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchLatestTransactionId } from '../api/grampsRest';
import { readBearerToken } from '../grampsAuth';

const POLL_INTERVAL_MS = 5000;

/**
 * Polls the Gramps transaction history every 5s for the latest transaction id.
 * The tree can change outside this tab (Gramps desktop, another browser tab or
 * user), so on any id change every cached query is invalidated to pick up the
 * edit.
 *
 * Each poll sends the last-seen transaction's timestamp as `after`, so the backend
 * can filter its history query down to "anything new" instead of weighing the whole
 * transaction history on every single poll. Until a first transaction has been seen
 * (right after mount, or after switching tree), there is no such timestamp yet — we
 * only care about changes going forward, not the tree's entire past history, so `after`
 * falls back to "one poll interval ago" instead of being left unbounded.
 */
export function useTransactionPolling(treeName: string | null): void {
    const queryClient = useQueryClient();
    const lastSeenId = useRef<number | undefined>(undefined);
    const lastSeenTimestamp = useRef<number | undefined>(undefined);

    const latestTransactionQuery = useQuery({
        queryKey: ['transactions', 'latest-id', treeName],
        // Read the token fresh on every poll rather than closing over a value
        // captured at render time: refetchInterval keeps firing on its own
        // without requiring this component to re-render, so a token rotated
        // elsewhere (e.g. by the background auto-refresh timer) would
        // otherwise never be picked up and every poll would keep using the
        // stale value until something unrelated happens to re-render this hook.
        queryFn: () => fetchLatestTransactionId(
            readBearerToken(treeName) ?? undefined,
            lastSeenTimestamp.current ?? Date.now() / 1000 - POLL_INTERVAL_MS / 1000,
        ),
        enabled: !!treeName,
        refetchInterval: POLL_INTERVAL_MS,
        refetchIntervalInBackground: true,
    });

    useEffect(() => {
        lastSeenId.current = undefined;
        lastSeenTimestamp.current = undefined;
    }, [treeName]);

    useEffect(() => {
        const latest = latestTransactionQuery.data;
        // undefined: query hasn't resolved yet; null: resolved, but nothing found in this window.
        if (latest === undefined || latest === null) return;
        // `after` is always scoped to a recent window (never unbounded), so any hit here
        // already represents a genuinely recent change — including the first one this hook
        // ever observes — and must invalidate, unlike a plain "different from last seen" check.
        if (latest.id !== lastSeenId.current) {
            void queryClient.invalidateQueries();
        }
        lastSeenId.current = latest.id;
        lastSeenTimestamp.current = latest.timestamp;
    }, [latestTransactionQuery.data, queryClient]);
}
